import React, { useMemo, useState, useContext, useEffect } from "react";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";
import { AuthContext } from "../../context/AuthContext";
import "./MesDocuments.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:5002";

const DRIVER_DOCS = [
  {
    key: "vtcCardRenewalRequest",
    title: "Demande de renouvellement carte VTC",
    description: "Demande de renouvellement de carte VTC.",
    requiresExpiry: true,
  },
  {
    key: "idCardVTCFront",
    title: "Carte VTC (recto)",
    description: "Face avant de la carte VTC.",
    requiresExpiry: true,
  },
  {
    key: "idCardVTCBack",
    title: "Carte VTC (verso)",
    description: "Face arrière de la carte VTC.",
    requiresExpiry: true,
  },
  {
    key: "adcs",
    title: "ADCS / Relevé d'information restreint",
    description:
      "Document obligatoire après un certain délai d'activité selon ton statut.",
    requiresExpiry: true,
  },
  {
    key: "insuranceCertificate",
    title: "Attestation RC Pro",
    description:
      "Attestation d’assurance responsabilité civile professionnelle.",
    requiresExpiry: true,
  },
  {
    key: "vtcOperatorRegister",
    title: "Registre exploitant VTC",
    description: "Uniquement pour les chauffeurs indépendants.",
    requiresExpiry: true,
  },
  {
    key: "drivingLicense",
    title: "Permis de conduire",
    description: "Permis de conduire valide.",
    requiresExpiry: true,
  },
  {
    key: "idCardFront",
    title: "Pièce d'identité (recto)",
    description: "Carte d'identité, passeport ou titre équivalent.",
    requiresExpiry: true,
  },
  {
    key: "idCardBack",
    title: "Pièce d'identité (verso)",
    description: "Carte d'identité, passeport ou titre équivalent.",
    requiresExpiry: true,
  },
  {
    key: "dpae",
    title: "DPAE",
    description: "Déclaration préalable à l’embauche.",
    requiresExpiry: false,
  },
  {
    key: "kbis",
    title: "KBIS / Avis SIRENE",
    description: "Uniquement pour les chauffeurs indépendants.",
    requiresExpiry: false,
  },
  {
    key: "rib",
    title: "RIB",
    description: "Relevé d'identité bancaire du chauffeur.",
    requiresExpiry: false,
  },
];

const VEHICLE_DOCS = [
  {
    key: "carteGrise",
    title: "Carte grise",
    description: "Certificat d'immatriculation du véhicule.",
    requiresExpiry: true,
  },
  {
    key: "assuranceMemo",
    title: "Assurance carte verte / mémo véhicule assuré",
    description:
      "Document avec plaque, date d’expiration et signature de l’assureur.",
    requiresExpiry: true,
  },
  {
    key: "macaronVTC",
    title: "Macaron VTC",
    description: "Document / justificatif du macaron VTC.",
    requiresExpiry: true,
  },
  {
    key: "attestationTransport",
    title: "Attestation transport à titre onéreux",
    description:
      'Mention visible : "transport de personnes à titre onéreux" / "activité VTC".',
    requiresExpiry: true,
  },
  {
    key: "vehicleExteriorPhoto",
    title: "Photo extérieure du véhicule",
    description: "Photo claire de l’extérieur du véhicule avec la plaque visible.",
    requiresExpiry: false,
  },
  {
    key: "vehicleInteriorPhoto",
    title: "Photo intérieure du véhicule",
    description: "Photo claire de l’intérieur du véhicule : sièges, propreté et habitacle.",
    requiresExpiry: false,
  },
];

const emptyExpiry = { year: "", month: "", day: "" };

const DEFAULT_BILLING_VEHICLE_FORM = {
  payout: {
    accountHolderName: "",
    iban: "",
    bic: "",
    bankName: "",
  },
  billingProfile: {
    companyName: "",
    legalName: "",
    siret: "",
    vatNumber: "",
    addressLine1: "",
    addressLine2: "",
    postalCode: "",
    city: "",
    country: "France",
  },
  vehicleProfile: {
    brand: "",
    model: "",
    year: "",
    color: "",
    plateNumber: "",
    seats: "",
  },
};

function buildYears() {
  const now = new Date().getFullYear();
  return Array.from({ length: 15 }, (_, i) => String(now + i));
}

function buildMonths() {
  return Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
}

function buildDays() {
  return Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));
}

function expiryToDateString(expiry) {
  if (!expiry?.year || !expiry?.month || !expiry?.day) return null;
  return `${expiry.year}-${expiry.month}-${expiry.day}`;
}

function dateStringToExpiry(dateString) {
  if (!dateString) return { ...emptyExpiry };
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return { ...emptyExpiry };

  return {
    year: String(d.getFullYear()),
    month: String(d.getMonth() + 1).padStart(2, "0"),
    day: String(d.getDate()).padStart(2, "0"),
  };
}

function isExpiryValid(expiry) {
  return !!expiry?.year && !!expiry?.month && !!expiry?.day;
}

function isExpired(dateString) {
  if (!dateString) return false;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return false;

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  return d < now;
}

function getExistingDocUrl(existingDoc) {
  if (!existingDoc) return null;
  if (typeof existingDoc === "string") return existingDoc;
  return existingDoc.url || null;
}

function getExistingDocFilename(existingDoc) {
  if (!existingDoc) return null;
  if (typeof existingDoc === "string") return null;
  return existingDoc.originalFilename || null;
}

function getExistingUploadedAt(existingDoc) {
  if (!existingDoc) return null;
  if (typeof existingDoc === "string") return null;
  return existingDoc.uploadedAt || null;
}

function getExistingExpiry(existingDoc) {
  if (!existingDoc) return { ...emptyExpiry };
  if (typeof existingDoc === "string") return { ...emptyExpiry };
  return dateStringToExpiry(existingDoc.expiryDate);
}

function getExistingStatus(existingDoc) {
  if (!existingDoc || typeof existingDoc === "string") return null;
  return existingDoc.status || null;
}

function getExistingAdminComment(existingDoc) {
  if (!existingDoc || typeof existingDoc === "string") return null;
  return existingDoc.adminComment || null;
}

function getStatusMeta({ selectedFile, existingDoc }) {
  const hasUploadedDoc = !!getExistingDocUrl(existingDoc);
  const docStatus = getExistingStatus(existingDoc);

  if (selectedFile && !hasUploadedDoc) {
    return { label: "Prêt à envoyer", className: "status-ready" };
  }

  if (!selectedFile && !hasUploadedDoc) {
    return { label: "À envoyer", className: "status-empty" };
  }

  if (docStatus === "validated") {
    return { label: "Validé", className: "status-validated" };
  }

  if (docStatus === "rejected") {
    return { label: "Rejeté", className: "status-rejected" };
  }

  if (docStatus === "pending") {
    return { label: "En attente", className: "status-pending" };
  }

  if (hasUploadedDoc) {
    return { label: "Envoyé", className: "status-sent" };
  }

  return { label: "Non traité", className: "status-neutral" };
}

function FilePreview({ selectedFile, existingDoc }) {
  const fileName =
    selectedFile?.name ||
    getExistingDocFilename(existingDoc) ||
    "Document uploadé";
  const url = getExistingDocUrl(existingDoc);

  if (!selectedFile && !url) {
    return <span className="doc-muted">—</span>;
  }

  return (
    <div className="doc-file-row">
      <span className="doc-file-name">📄 {fileName}</span>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" className="doc-file-link">
          Voir
        </a>
      ) : null}
    </div>
  );
}

function ExpiryInputs({ value, onChange, disabled = false }) {
  const years = useMemo(() => buildYears(), []);
  const months = useMemo(() => buildMonths(), []);
  const days = useMemo(() => buildDays(), []);

  return (
    <div className="expiry-grid">
      <select
        value={value.year}
        disabled={disabled}
        onChange={(e) => onChange({ ...value, year: e.target.value })}
      >
        <option value="">Année</option>
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>

      <select
        value={value.month}
        disabled={disabled}
        onChange={(e) => onChange({ ...value, month: e.target.value })}
      >
        <option value="">Mois</option>
        {months.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>

      <select
        value={value.day}
        disabled={disabled}
        onChange={(e) => onChange({ ...value, day: e.target.value })}
      >
        <option value="">Jour</option>
        {days.map((d) => (
          <option key={d} value={d}>
            {d}
          </option>
        ))}
      </select>
    </div>
  );
}

function DocumentCard({
  item,
  state,
  onPickFile,
  onExpiryChange,
  onSave,
  onDelete,
  saving,
  deleting,
  aosDelay = 0,
}) {
  const existingDoc = state?.uploadedDoc || null;
  const hasUploadedDoc = !!getExistingDocUrl(existingDoc);
  const hasPendingFile = !!state?.selectedFile;
  const uploadedAt = getExistingUploadedAt(existingDoc)
    ? new Date(getExistingUploadedAt(existingDoc)).toLocaleString("fr-FR")
    : "—";
  const expiryText = expiryToDateString(state?.expiry) || "—";
  const expired = isExpired(
    typeof existingDoc === "object" ? existingDoc?.expiryDate : null
  );
  const adminComment = getExistingAdminComment(existingDoc);
  const statusMeta = getStatusMeta({
    selectedFile: state?.selectedFile,
    existingDoc,
  });

  return (
    <div className="doc-card" data-aos="fade-up" data-aos-delay={aosDelay}>
      {expired ? <div className="doc-expired-banner">Document expiré</div> : null}

      <div className="doc-card-top">
        <h3>{item.title}</h3>
        {item.requiresExpiry ? (
          <span className="doc-badge">Expiration requise</span>
        ) : null}
      </div>

      {item.description ? <p className="doc-desc">{item.description}</p> : null}

      {item.requiresExpiry ? (
        <>
          <label className="doc-label">Date d’expiration</label>
          <ExpiryInputs value={state.expiry} onChange={onExpiryChange} />
        </>
      ) : null}

      <div className="doc-meta-block">
        <div>
          <span className="doc-label">Fichier</span>
          <FilePreview selectedFile={state?.selectedFile} existingDoc={existingDoc} />
        </div>

        <div className="doc-meta-lines">
          <div className="doc-meta">Uploadé : {uploadedAt}</div>
          <div className="doc-meta">Expire le : {expiryText}</div>
          <div className="doc-meta">
            Statut :{" "}
            <span className={`doc-status-pill ${statusMeta.className}`}>
              {statusMeta.label}
            </span>
          </div>
        </div>
      </div>

      {adminComment ? (
        <div className="doc-admin-comment">
          <strong>Retour admin :</strong> {adminComment}
        </div>
      ) : null}

      <div className="doc-actions">
        <label className={`upload-btn ${saving ? "is-disabled" : ""}`}>
          {hasPendingFile ? "Fichier sélectionné" : "＋ Choisir un fichier"}
          <input
            type="file"
            accept=".pdf,image/*"
            hidden
            disabled={saving || deleting}
            onChange={(e) => onPickFile(e.target.files?.[0] || null)}
          />
        </label>

        <button
          type="button"
          className="submit-btn"
          disabled={saving || deleting || !hasPendingFile}
          onClick={onSave}
        >
          {saving ? "Enregistrement..." : "Envoyer ce document"}
        </button>

        <button
          type="button"
          className="delete-btn"
          disabled={saving || deleting || !hasUploadedDoc}
          onClick={onDelete}
        >
          {deleting ? "Suppression..." : "Supprimer"}
        </button>
      </div>
    </div>
  );
}

function makeInitialState(allDocs) {
  return allDocs.reduce((acc, d) => {
    acc[d.key] = {
      selectedFile: null,
      uploadedDoc: null,
      expiry: { ...emptyExpiry },
    };
    return acc;
  }, {});
}

export default function MesDocuments() {
  const { isAuthenticated } = useContext(AuthContext);

  const [userInfo, setUserInfo] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const [savingKey, setSavingKey] = useState("");
  const [deletingKey, setDeletingKey] = useState("");

  const [driverDocs, setDriverDocs] = useState(makeInitialState(DRIVER_DOCS));
  const [vehicleDocs, setVehicleDocs] = useState(makeInitialState(VEHICLE_DOCS));

  const [billingVehicleForm, setBillingVehicleForm] = useState(
    DEFAULT_BILLING_VEHICLE_FORM
  );

  const [savingBillingVehicle, setSavingBillingVehicle] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      mirror: false,
      disable: window.innerWidth < 768,
    });
  }, []);

  // useEffect(() => {
  //   AOS.refresh();
  // }, [
  //   driverDocs,
  //   vehicleDocs,
  //   loadingProfile,
  //   pageMessage,
  //   billingVehicleForm,
  // ]);
  useEffect(() => {
  if (window.innerWidth >= 768) {
    AOS.refresh();
  }
}, [loadingProfile]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (!isAuthenticated || !token) {
          setLoadingProfile(false);
          return;
        }

        const res = await axios.get(`${API}/api/chauffeurs/profile-chauffeur`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const chauffeur = res.data || {};
        setUserInfo(chauffeur);

        setBillingVehicleForm({
          payout: {
            accountHolderName: chauffeur.payout?.accountHolderName || "",
            iban: chauffeur.payout?.iban || "",
            bic: chauffeur.payout?.bic || "",
            bankName: chauffeur.payout?.bankName || "",
          },
          billingProfile: {
            companyName: chauffeur.billingProfile?.companyName || "",
            legalName: chauffeur.billingProfile?.legalName || "",
            siret: chauffeur.billingProfile?.siret || "",
            vatNumber: chauffeur.billingProfile?.vatNumber || "",
            addressLine1: chauffeur.billingProfile?.addressLine1 || "",
            addressLine2: chauffeur.billingProfile?.addressLine2 || "",
            postalCode: chauffeur.billingProfile?.postalCode || "",
            city: chauffeur.billingProfile?.city || "",
            country: chauffeur.billingProfile?.country || "France",
          },
          vehicleProfile: {
            brand: chauffeur.vehicleProfile?.brand || chauffeur.carBrand || "",
            model: chauffeur.vehicleProfile?.model || chauffeur.carModel || "",
            year: chauffeur.vehicleProfile?.year || chauffeur.vehicleYear || "",
            color: chauffeur.vehicleProfile?.color || chauffeur.carColor || "",
            plateNumber:
              chauffeur.vehicleProfile?.plateNumber || chauffeur.plateNumber || "",
            seats: chauffeur.vehicleProfile?.seats || chauffeur.seats || "",
          },
        });
      } catch (err) {
        console.error("Erreur profil chauffeur:", err);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [isAuthenticated, token]);

  const chauffeurId = userInfo?._id || userInfo?.id || userInfo?.chauffeurId || null;

  useEffect(() => {
    const fetchExistingDocuments = async () => {
      try {
        if (!chauffeurId || !token) return;

        const res = await axios.get(
          `${API}/api/chauffeurs/${chauffeurId}/documents`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const existingDocs = res.data?.documents || {};

        setDriverDocs((prev) => {
          const next = { ...prev };
          for (const doc of DRIVER_DOCS) {
            const existingDoc = existingDocs?.[doc.key] || null;
            next[doc.key] = {
              ...next[doc.key],
              uploadedDoc: existingDoc,
              expiry: getExistingExpiry(existingDoc),
              selectedFile: null,
            };
          }
          return next;
        });

        setVehicleDocs((prev) => {
          const next = { ...prev };
          for (const doc of VEHICLE_DOCS) {
            const existingDoc = existingDocs?.[doc.key] || null;
            next[doc.key] = {
              ...next[doc.key],
              uploadedDoc: existingDoc,
              expiry: getExistingExpiry(existingDoc),
              selectedFile: null,
            };
          }
          return next;
        });
      } catch (err) {
        console.error("Erreur récupération documents:", err);
      }
    };

    fetchExistingDocuments();
  }, [chauffeurId, token]);

  const updateDocState = (group, key, updater) => {
    const setter = group === "driver" ? setDriverDocs : setVehicleDocs;
    setter((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...updater(prev[key]),
      },
    }));
  };

  const updateBillingField = (field, value) => {
    setBillingVehicleForm((prev) => ({
      ...prev,
      payout: {
        ...prev.payout,
        [field]: value,
      },
    }));
  };

  const updateInvoiceField = (field, value) => {
    setBillingVehicleForm((prev) => ({
      ...prev,
      billingProfile: {
        ...prev.billingProfile,
        [field]: value,
      },
    }));
  };

  const updateVehicleField = (field, value) => {
    setBillingVehicleForm((prev) => ({
      ...prev,
      vehicleProfile: {
        ...prev.vehicleProfile,
        [field]: value,
      },
    }));
  };

  const saveBillingVehicle = async () => {
    try {
      setSavingBillingVehicle(true);
      setPageMessage("");

      await axios.put(
        `${API}/api/chauffeurs/me/billing-vehicle`,
        {
          payout: billingVehicleForm.payout,
          billingProfile: billingVehicleForm.billingProfile,
          vehicleProfile: {
            ...billingVehicleForm.vehicleProfile,
            year: billingVehicleForm.vehicleProfile.year
              ? Number(billingVehicleForm.vehicleProfile.year)
              : null,
            seats: billingVehicleForm.vehicleProfile.seats
              ? Number(billingVehicleForm.vehicleProfile.seats)
              : null,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPageMessage(
        "Informations de facturation, virement et véhicule enregistrées ✅"
      );
    } catch (err) {
      console.error("Erreur sauvegarde infos facturation/véhicule:", err);
      setPageMessage(
        err?.response?.data?.message ||
          "Impossible d’enregistrer les informations."
      );
    } finally {
      setSavingBillingVehicle(false);
    }
  };

  const handlePickFile = (group, docKey, file) => {
    if (!file) return;

    updateDocState(group, docKey, (prev) => ({
      selectedFile: file,
      uploadedDoc: prev.uploadedDoc,
    }));
  };

  const handleExpiryChange = (group, docKey, expiry) => {
    updateDocState(group, docKey, () => ({
      expiry,
    }));
  };

  const saveDocument = async (group, item) => {
    const docsState = group === "driver" ? driverDocs : vehicleDocs;
    const current = docsState[item.key];

    if (!current?.selectedFile) {
      setPageMessage("Choisis d’abord un fichier avant d’enregistrer.");
      return;
    }

    if (item.requiresExpiry && !isExpiryValid(current.expiry)) {
      setPageMessage(`Merci de renseigner la date d’expiration pour : ${item.title}`);
      return;
    }

    try {
      setPageMessage("");
      setSavingKey(`${group}-${item.key}`);

      const formData = new FormData();
      formData.append("file", current.selectedFile);

      const expiryDate = expiryToDateString(current.expiry);
      if (expiryDate) {
        formData.append("expiryDate", expiryDate);
      }

      const res = await axios.post(
        `${API}/api/chauffeurs/${chauffeurId}/documents/${item.key}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const savedDoc = res.data?.document || res.data?.documents?.[item.key] || null;

      updateDocState(group, item.key, () => ({
        selectedFile: null,
        uploadedDoc:
          typeof savedDoc === "string"
            ? savedDoc
            : savedDoc || {
                url: res.data?.url || null,
                originalFilename: current.selectedFile?.name || null,
                uploadedAt: new Date().toISOString(),
                expiryDate: expiryDate || null,
                status: "pending",
                adminComment: null,
              },
      }));

      setPageMessage(`${item.title} envoyé avec succès ✅`);
    } catch (err) {
      console.error(`Erreur enregistrement ${item.key}:`, err);
      setPageMessage(
        err?.response?.data?.message ||
          `Impossible d’envoyer le document : ${item.title}`
      );
    } finally {
      setSavingKey("");
    }
  };

  const deleteDocument = async (group, item) => {
    const docsState = group === "driver" ? driverDocs : vehicleDocs;
    const current = docsState[item.key];

    const hasUploaded = !!getExistingDocUrl(current?.uploadedDoc);
    const hasSelected = !!current?.selectedFile;

    if (!hasUploaded && !hasSelected) {
      setPageMessage("Aucun document à supprimer.");
      return;
    }

    const ok = window.confirm(`Supprimer le document "${item.title}" ?`);
    if (!ok) return;

    try {
      setPageMessage("");
      setDeletingKey(`${group}-${item.key}`);

      if (hasUploaded) {
        await axios.delete(
          `${API}/api/chauffeurs/${chauffeurId}/documents/${item.key}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      updateDocState(group, item.key, () => ({
        selectedFile: null,
        uploadedDoc: null,
        expiry: { ...emptyExpiry },
      }));

      setPageMessage(`${item.title} supprimé avec succès ✅`);
    } catch (err) {
      console.error(`Erreur suppression ${item.key}:`, err);
      setPageMessage(
        err?.response?.data?.message ||
          `Impossible de supprimer le document : ${item.title}`
      );
    } finally {
      setDeletingKey("");
    }
  };

  const driverUploadedCount = DRIVER_DOCS.filter(
    (doc) => !!getExistingDocUrl(driverDocs[doc.key]?.uploadedDoc)
  ).length;

  const vehicleUploadedCount = VEHICLE_DOCS.filter(
    (doc) => !!getExistingDocUrl(vehicleDocs[doc.key]?.uploadedDoc)
  ).length;

  if (!isAuthenticated) {
    return (
      <div className="docs-page">
        <div className="docs-shell">
          <div className="docs-hero" data-aos="fade-up">
            <h1>Mes Documents</h1>
            <p>Vous devez être connecté pour accéder à cette page.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loadingProfile) {
    return (
      <div className="docs-page">
        <div className="docs-shell">
          <div className="docs-hero" data-aos="fade-up">
            <h1>Mes Documents</h1>
            <p>Chargement du profil chauffeur...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!chauffeurId) {
    return (
      <div className="docs-page">
        <div className="docs-shell">
          <div className="docs-hero" data-aos="fade-up">
            <h1>Mes Documents</h1>
            <p>Impossible de récupérer l'identifiant du chauffeur.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="docs-page">
      <div className="docs-shell">
        <div className="docs-hero" data-aos="fade-up">
          <h1>Mes Documents</h1>
          <p>
            Envoie tes documents un par un, renseigne tes informations de
            facturation et consulte leur statut de validation.
          </p>
        </div>

        {pageMessage ? (
          <div className="docs-global-message" data-aos="fade-up">
            {pageMessage}
          </div>
        ) : null}

        <section className="docs-section" data-aos="fade-up" data-aos-delay="80">
          <div className="docs-section-head">
            <div>
              <h2>Informations de facturation & véhicule</h2>
              <p>
                Ces informations permettent à Dieli de préparer les virements
                chauffeur, les factures de service et la vérification du véhicule.
              </p>
            </div>
          </div>

          <div className="doc-card billing-card">
            <div className="doc-card-top">
              <h3>Coordonnées bancaires</h3>
              <span className="doc-badge">Virement société</span>
            </div>

            <div className="docs-form-grid">
              <div>
                <label className="doc-label">Titulaire du compte</label>
                <input
                  className="doc-input"
                  value={billingVehicleForm.payout.accountHolderName}
                  onChange={(e) =>
                    updateBillingField("accountHolderName", e.target.value)
                  }
                  placeholder="Ex : Alexandre Medor"
                />
              </div>

              <div>
                <label className="doc-label">IBAN</label>
                <input
                  className="doc-input"
                  value={billingVehicleForm.payout.iban}
                  onChange={(e) => updateBillingField("iban", e.target.value)}
                  placeholder="FR76..."
                />
              </div>

              <div>
                <label className="doc-label">BIC</label>
                <input
                  className="doc-input"
                  value={billingVehicleForm.payout.bic}
                  onChange={(e) => updateBillingField("bic", e.target.value)}
                  placeholder="Ex : BNPAFRPP"
                />
              </div>

              <div>
                <label className="doc-label">Nom de la banque</label>
                <input
                  className="doc-input"
                  value={billingVehicleForm.payout.bankName}
                  onChange={(e) => updateBillingField("bankName", e.target.value)}
                  placeholder="Ex : BNP Paribas"
                />
              </div>
            </div>
          </div>

          <div className="doc-card billing-card">
            <div className="doc-card-top">
              <h3>Profil de facturation</h3>
              <span className="doc-badge">SIRET obligatoire</span>
            </div>

            <div className="docs-form-grid">
              <div>
                <label className="doc-label">Nom commercial / société</label>
                <input
                  className="doc-input"
                  value={billingVehicleForm.billingProfile.companyName}
                  onChange={(e) =>
                    updateInvoiceField("companyName", e.target.value)
                  }
                  placeholder="Ex : Dieli Driver Services"
                />
              </div>

              <div>
                <label className="doc-label">Nom légal / titulaire</label>
                <input
                  className="doc-input"
                  value={billingVehicleForm.billingProfile.legalName}
                  onChange={(e) => updateInvoiceField("legalName", e.target.value)}
                  placeholder="Ex : Alexandre Medor"
                />
              </div>

              <div>
                <label className="doc-label">Numéro SIRET</label>
                <input
                  className="doc-input"
                  value={billingVehicleForm.billingProfile.siret}
                  onChange={(e) => updateInvoiceField("siret", e.target.value)}
                  placeholder="Ex : 12345678900012"
                />
              </div>

              <div>
                <label className="doc-label">Numéro TVA intracommunautaire</label>
                <input
                  className="doc-input"
                  value={billingVehicleForm.billingProfile.vatNumber}
                  onChange={(e) =>
                    updateInvoiceField("vatNumber", e.target.value)
                  }
                  placeholder="Optionnel - Ex : FR00123456789"
                />
              </div>

              <div>
                <label className="doc-label">Adresse de facturation</label>
                <input
                  className="doc-input"
                  value={billingVehicleForm.billingProfile.addressLine1}
                  onChange={(e) =>
                    updateInvoiceField("addressLine1", e.target.value)
                  }
                  placeholder="Ex : 10 rue de Paris"
                />
              </div>

              <div>
                <label className="doc-label">Complément d’adresse</label>
                <input
                  className="doc-input"
                  value={billingVehicleForm.billingProfile.addressLine2}
                  onChange={(e) =>
                    updateInvoiceField("addressLine2", e.target.value)
                  }
                  placeholder="Bâtiment, étage, appartement..."
                />
              </div>

              <div>
                <label className="doc-label">Code postal</label>
                <input
                  className="doc-input"
                  value={billingVehicleForm.billingProfile.postalCode}
                  onChange={(e) =>
                    updateInvoiceField("postalCode", e.target.value)
                  }
                  placeholder="Ex : 75001"
                />
              </div>

              <div>
                <label className="doc-label">Ville</label>
                <input
                  className="doc-input"
                  value={billingVehicleForm.billingProfile.city}
                  onChange={(e) => updateInvoiceField("city", e.target.value)}
                  placeholder="Ex : Paris"
                />
              </div>

              <div>
                <label className="doc-label">Pays</label>
                <input
                  className="doc-input"
                  value={billingVehicleForm.billingProfile.country}
                  onChange={(e) => updateInvoiceField("country", e.target.value)}
                  placeholder="France"
                />
              </div>
            </div>
          </div>

          <div className="doc-card billing-card">
            <div className="doc-card-top">
              <h3>Véhicule utilisé</h3>
              <span className="doc-badge">Photo modifiable par l’admin</span>
            </div>

            <div className="docs-form-grid">
              <div>
                <label className="doc-label">Marque</label>
                <input
                  className="doc-input"
                  list="car-brands"
                  value={billingVehicleForm.vehicleProfile.brand}
                  onChange={(e) => updateVehicleField("brand", e.target.value)}
                  placeholder="Ex : Mercedes"
                />

                <datalist id="car-brands">
                  <option value="Mercedes" />
                  <option value="BMW" />
                  <option value="Audi" />
                  <option value="Tesla" />
                  <option value="Toyota" />
                  <option value="Peugeot" />
                  <option value="Renault" />
                  <option value="Volkswagen" />
                  <option value="Hyundai" />
                  <option value="Kia" />
                </datalist>
              </div>

              <div>
                <label className="doc-label">Modèle</label>
                <input
                  className="doc-input"
                  list="car-models"
                  value={billingVehicleForm.vehicleProfile.model}
                  onChange={(e) => updateVehicleField("model", e.target.value)}
                  placeholder="Ex : Classe E"
                />

                <datalist id="car-models">
                  <option value="Classe E" />
                  <option value="Classe V" />
                  <option value="Série 5" />
                  <option value="A6" />
                  <option value="Model 3" />
                  <option value="Model Y" />
                  <option value="Prius" />
                  <option value="508" />
                  <option value="Talisman" />
                  <option value="Arteon" />
                </datalist>
              </div>

              <div>
                <label className="doc-label">Année</label>
                <input
                  className="doc-input"
                  type="number"
                  value={billingVehicleForm.vehicleProfile.year}
                  onChange={(e) => updateVehicleField("year", e.target.value)}
                  placeholder="Ex : 2022"
                />
              </div>

              <div>
                <label className="doc-label">Couleur</label>
                <input
                  className="doc-input"
                  value={billingVehicleForm.vehicleProfile.color}
                  onChange={(e) => updateVehicleField("color", e.target.value)}
                  placeholder="Ex : Noir"
                />
              </div>

              <div>
                <label className="doc-label">Plaque d’immatriculation</label>
                <input
                  className="doc-input"
                  value={billingVehicleForm.vehicleProfile.plateNumber}
                  onChange={(e) =>
                    updateVehicleField("plateNumber", e.target.value)
                  }
                  placeholder="Ex : AB-123-CD"
                />
              </div>

              <div>
                <label className="doc-label">Nombre de places</label>
                <input
                  className="doc-input"
                  type="number"
                  min="1"
                  max="9"
                  value={billingVehicleForm.vehicleProfile.seats}
                  onChange={(e) => updateVehicleField("seats", e.target.value)}
                  placeholder="Ex : 4"
                />
              </div>
            </div>

            <div className="doc-actions">
              <button
                type="button"
                className="submit-btn"
                disabled={savingBillingVehicle}
                onClick={saveBillingVehicle}
              >
                {savingBillingVehicle
                  ? "Enregistrement..."
                  : "Enregistrer les informations"}
              </button>
            </div>
          </div>
        </section>

        <section className="docs-section" data-aos="fade-up" data-aos-delay="100">
          <div className="docs-section-head">
            <div>
              <h2>Documents chauffeur</h2>
              <p>Chaque document est traité séparément par l’admin.</p>
              <p className="doc-meta">
                Déjà envoyés : {driverUploadedCount} / {DRIVER_DOCS.length}
              </p>
            </div>
          </div>

          <div className="docs-grid">
            {DRIVER_DOCS.map((doc, index) => (
              <DocumentCard
                key={doc.key}
                item={doc}
                state={driverDocs[doc.key]}
                saving={savingKey === `driver-${doc.key}`}
                deleting={deletingKey === `driver-${doc.key}`}
                onPickFile={(file) => handlePickFile("driver", doc.key, file)}
                onExpiryChange={(expiry) =>
                  handleExpiryChange("driver", doc.key, expiry)
                }
                onSave={() => saveDocument("driver", doc)}
                onDelete={() => deleteDocument("driver", doc)}
                aosDelay={index * 40}
              />
            ))}
          </div>
        </section>

        <section className="docs-section" data-aos="fade-up" data-aos-delay="150">
          <div className="docs-section-head">
            <div>
              <h2>Documents véhicule</h2>
              <p>Les documents véhicule sont aussi validés unitairement.</p>
              <p className="doc-meta">
                Déjà envoyés : {vehicleUploadedCount} / {VEHICLE_DOCS.length}
              </p>
            </div>
          </div>

          <div className="docs-grid">
            {VEHICLE_DOCS.map((doc, index) => (
              <DocumentCard
                key={doc.key}
                item={doc}
                state={vehicleDocs[doc.key]}
                saving={savingKey === `vehicle-${doc.key}`}
                deleting={deletingKey === `vehicle-${doc.key}`}
                onPickFile={(file) => handlePickFile("vehicle", doc.key, file)}
                onExpiryChange={(expiry) =>
                  handleExpiryChange("vehicle", doc.key, expiry)
                }
                onSave={() => saveDocument("vehicle", doc)}
                onDelete={() => deleteDocument("vehicle", doc)}
                aosDelay={index * 40}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}